import {
  forwardRef,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  type TextareaHTMLAttributes,
} from 'react';
import { HelpTip } from '../HelpTip';
import { useDeferredError } from './useDeferredError';

interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'rows'> {
  label: string;
  /**
   * Shown in the label's help icon, never as a line of its own. Still reaches screen readers
   * through an `sr-only` node wired to `aria-describedby`.
   */
  description?: string;
  /** Field-level message from the server or client validation. */
  error?: string;
  /** Required for a stable, non-index-based DOM id. Falls back to a generated one. */
  id?: string;
  /** Lines shown before it grows. The floor, never the ceiling. */
  minRows?: number;
  /**
   * Lines it will grow to before it starts scrolling instead.
   *
   * **A ceiling is not optional.** Unbounded, a long value grows the field until the modal's
   * own footer — and the confirm tick above it — scroll out of reach, which is the failure
   * `<RichTextField>`'s capped scroll region already exists to prevent.
   */
  maxRows?: number;
  /**
   * Render the control ALONE — no label row, no help icon.
   *
   * The caller then owes a real `<label htmlFor>`, and `label` still becomes the `aria-label`,
   * so it must match the caller's visible text exactly. Pass `description` as well when there
   * is one: this renders no help icon but still renders the `sr-only` twin.
   */
  labelHidden?: boolean;
}

/**
 * The shared multi-line text input — reference §5, "multi-line uses auto-grow `<Textarea>`".
 *
 * **It is for prose that is not a DOCUMENT.** A narrative somebody types into a form: a case
 * description, an action taken, a note. The two neighbours it must not be confused with:
 *
 * | | For |
 * |---|---|
 * | `<TextField>` | one line — a name, a code, a number, anything a table renders in a cell |
 * | `<Textarea>` | several lines of plain prose the reader types and moves on from |
 * | `<RichTextField>` | a DOCUMENT — prose someone writes, re-reads and STRUCTURES |
 *
 * Reaching past this for `<RichTextField>` buys a write-time sanitizer, a widened column and
 * 450 kB of JavaScript to store a paragraph — that component's own rule says it is never the
 * default, and this is the control that rule assumes exists.
 *
 * **It keeps every promise the other shared inputs make**, because a field that looked like
 * the rest of the form and behaved differently would be worse than an obviously bespoke one:
 * the description goes in a `<HelpTip>` with its `sr-only` twin, the error stacks below the
 * control in the flow, and the error is DEFERRED — a required-but-empty field does not open
 * red. A new input that renders an error and does not call `useDeferredError` is a defect.
 *
 * **It grows with its content rather than scrolling from the first line.** A fixed three-row
 * box makes the reader scroll a value they are still writing; `minRows` is the floor and
 * `maxRows` the ceiling past which it scrolls instead.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    description,
    error,
    labelHidden = false,
    id,
    minRows = 3,
    maxRows = 10,
    className = '',
    required,
    value,
    ...rest
  },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;

  const { error: shownError, markTouched } = useDeferredError(error);

  const inner = useRef<HTMLTextAreaElement | null>(null);

  /*
    THE AUTO-GROW, and it is measured rather than counted.

    Counting newlines is the version everyone writes first and it is wrong on the case that
    matters: a long paragraph with no newline in it wraps to six lines and counts as one. So
    the height is read off `scrollHeight` — the browser's own answer to "how tall is this
    content" — with the box collapsed to `auto` first, because `scrollHeight` never shrinks
    below a height already set.
  */
  const resize = useCallback(() => {
    const element = inner.current;
    if (!element) return;

    const styles = window.getComputedStyle(element);
    const line = Number.parseFloat(styles.lineHeight) || 20;
    const padding =
      Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom) || 0;
    const border =
      Number.parseFloat(styles.borderTopWidth) + Number.parseFloat(styles.borderBottomWidth) || 0;

    element.style.height = 'auto';

    const content = element.scrollHeight;
    const floor = line * minRows + padding + border;
    const ceiling = line * maxRows + padding + border;

    element.style.height = `${Math.min(Math.max(content, floor), ceiling)}px`;
    element.style.overflowY = content > ceiling ? 'auto' : 'hidden';
  }, [minRows, maxRows]);

  /*
    A LAYOUT effect, not a passive one: the height has to be right in the same frame the value
    paints, or a form seeded from a record visibly jumps from three rows to eight after its
    first paint. It depends on `value` so a controlled field re-measures as the reader types,
    and on `resize` so changing the bounds re-measures too.
  */
  useLayoutEffect(resize, [resize, value]);

  const describedBy = [
    description ? `${fieldId}-desc` : null,
    shownError ? `${fieldId}-err` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      {labelHidden ? null : (
        <div className="flex min-h-5 items-center gap-1.5">
          <label htmlFor={fieldId} className="text-sm text-slate-700">
            {label}
            {required ? (
              <span className="ml-0.5 text-red-600" aria-hidden="true">
                *
              </span>
            ) : null}
          </label>

          {description ? <HelpTip label={description} /> : null}
        </div>
      )}

      {description ? (
        <span id={`${fieldId}-desc`} className="sr-only">
          {description}
        </span>
      ) : null}

      <textarea
        {...rest}
        ref={(element) => {
          inner.current = element;

          // Both the local ref and the caller's, without clobbering either form.
          if (typeof ref === 'function') ref(element);
          else if (ref) ref.current = element;
        }}
        id={fieldId}
        name={rest.name ?? fieldId}
        value={value}
        required={required}
        aria-invalid={shownError ? true : undefined}
        aria-label={labelHidden ? label : undefined}
        aria-describedby={describedBy || undefined}
        onInput={(e) => {
          resize();
          rest.onInput?.(e);
        }}
        onBlur={(e) => {
          markTouched();
          rest.onBlur?.(e);
        }}
        /*
          `modern-scrollbar` because this owns a scroll region once it hits its ceiling, and
          every overflow container in the product carries it — one without is a panel with a
          platform scrollbar that ignores the product's own rule.

          `resize-none` because the auto-grow already does the job, and a drag handle would
          fight it: the next keystroke re-measures and undoes whatever the reader dragged.
        */
        className={[
          'modern-scrollbar block w-full resize-none rounded-lg border bg-white px-3 py-2 text-sm',
          'text-slate-600 placeholder:text-slate-400 focus:outline-none focus:ring-2',
          shownError
            ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
            : 'border-slate-300 focus:border-blue-500 focus:ring-blue-100',
          'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          className,
        ].join(' ')}
      />

      {/* An error is not an aside: below the control, in the flow, impossible to miss. */}
      {shownError ? (
        <p id={`${fieldId}-err`} className="text-xs text-red-600">
          {shownError}
        </p>
      ) : null}
    </div>
  );
});
